import { create } from "zustand";

import { Platform } from "react-native";

import { authApi, chatApi, usersApi, webAuthApi } from "@/api";
import type { WebAuthSession } from "@/api";
import {
  ApiError,
  setAccessToken,
  setRefreshHandler,
  setUnauthorizedHandler,
} from "@/api/client";
import { getCurrentAppLanguage } from "@/i18n";
import { clearPresenceAnchor } from "@/lib/presence-anchor-io";
import { resealKeychain, storage } from "@/lib/storage";
import { resetRegisteredSessionState } from "@/state/session-reset";
import { isIgnorableRefusal } from "@/lib/ignorable-refusal";
import type { AuthSession, Mode, User, UserAvatarConfig } from "@/types";

const SESSION_KEY = "opncue.session";
const USER_KEY = "opncue.user";
const ONBOARDING_KEY = "opncue.onboarding";
/**
 * Which email the code we just sent belongs to.
 *
 * On disk rather than in memory alone, because the one thing a user MUST do
 * between asking for a code and typing it is leave the app to read their email
 * — and both mobile platforms reclaim a backgrounded app, especially when a
 * heavy mail client comes forward. Expo Router restores the verify ROUTE after
 * that; nothing restored the address, so `verify()` threw before it made a
 * request and the screen reported a correct code as wrong. Every retry did the
 * same, because the missing value never came back.
 *
 * This is state loss, not an Android defect, and it is NOT the whole of the
 * reported bug: the same symptom was later reproduced without ever leaving the
 * app, by a duplicate `/auth/start` invalidating the delivered code. See
 * `lib/single-tap.ts` and `auth.otp-single-code.spec.ts` for that half.
 */
const PENDING_EMAIL_KEY = "opncue.pendingEmail";

/**
 * How long a stored pending email is worth restoring.
 *
 * Comfortably longer than the code's own ten minutes, on purpose: restoring an
 * address whose code has since expired lets the server say "expired" and the
 * screen offer a resend, which is a working path. Dropping the address instead
 * leaves the user where this bug left them, with no way forward but to start
 * over and no explanation.
 */
const PENDING_EMAIL_TTL_MS = 15 * 60 * 1000;

/**
 * We do not know which email the code in the user's hand belongs to.
 *
 * A distinct type because the screen has to tell it apart from a code the
 * server rejected. Both used to arrive at the same blanket `catch` and print
 * "That code did not work" — which was false, and worse, unactionable: the code
 * was fine, and no number of retries could make an address reappear.
 */
export class MissingPendingEmailError extends Error {
  constructor() {
    super("No pending email to verify.");
    this.name = "MissingPendingEmailError";
  }
}

type OnboardingProgress = {
  permissionsDone: boolean;
  modeDone: boolean;
};

/**
 * `unavailable` is "the session is fine, we just cannot load who you are".
 *
 * It exists because the alternative was a lie. A valid session with no cached
 * user and a `/users/me` that failed for any non-401 reason used to be
 * published as `authenticated` with `user: null`, and `needsProfileSetup(null)`
 * is `true` — so a fully signed-up person was told their profile was
 * incomplete and sent to onboarding. That is how a Cloudflare Access
 * interception presented itself (see `api/response.ts`), and it would present
 * the same way on a captive portal.
 *
 * The two wrong answers it avoids: routing to onboarding (says something false
 * about the account) and clearing the session (destroys credentials that are
 * probably valid, over what is usually a transient network condition).
 */
export type AuthStatus =
  | "loading"
  | "unauthenticated"
  | "authenticated"
  | "unavailable";

type AuthState = {
  status: AuthStatus;
  session: AuthSession | null;
  user: User | null;
  pendingEmail: string | null;
  onboarding: OnboardingProgress;

  hydrate: () => Promise<void>;
  startEmail: (email: string) => Promise<void>;
  /**
   * Recover the address a code was sent to, after the process was killed.
   *
   * `candidate` is the route's own copy — Expo Router restores params with the
   * route, so it survives exactly the event that loses the store. Falls back to
   * the short-lived record on disk. Returns what it found, and puts it back in
   * the store so `verify` and `onResend` work again.
   */
  restorePendingEmail: (candidate?: string | null) => Promise<string | null>;
  verify: (code: string) => Promise<void>;
  signInWithGoogle: (idToken: string) => Promise<void>;
  signInWithApple: (
    identityToken: string,
    fullName?: string,
    nonce?: string,
  ) => Promise<void>;
  refreshUser: () => Promise<void>;
  setProfile: (patch: Partial<User>) => Promise<void>;
  /** Save a customized illustrated avatar config. */
  setAvatar: (avatarConfig: UserAvatarConfig) => Promise<void>;
  /** DEV ONLY: force the account's plan to test Plus-gated features. No-op in production. */
  setDevPlan: (plan: NonNullable<User["plan"]>) => Promise<void>;
  setMode: (mode: Mode) => Promise<void>;
  skipMode: () => Promise<void>;
  completePermissions: () => Promise<void>;
  clearLocalSession: () => Promise<void>;
  signOut: () => Promise<void>;
  /** Permanently delete the account server-side, then drop the local session. */
  setAccountDeactivated: (deactivated: boolean) => Promise<void>;
  /** Open to chat: a duration in minutes turns it on, `null` turns it off. */
  setOpenToChat: (durationMinutes: number | null) => Promise<void>;
  deleteAccount: () => Promise<void>;
};

/**
 * No step taken yet.
 *
 * Both the fallback when nothing is stored AND what sign-out resets to, which
 * are the same fact and must stay one constant: a second copy of "empty" is a
 * second place for `permissionsDone` to quietly become true.
 */
const defaultOnboarding: OnboardingProgress = {
  permissionsDone: false,
  modeDone: false,
};

/**
 * On web the session is never persisted (stage 3, 2026-09-02): the refresh
 * token lives in an HttpOnly cookie the page cannot read, the access token
 * lives in memory only, and a reload rebuilds both through
 * webAuthApi.refresh(). The unconditional remove also migrates away any
 * session an earlier build left in localStorage — which was the audit
 * finding this design exists to close.
 */
const WEB_COOKIE_AUTH = Platform.OS === "web";

/** The web responses carry no refresh token; the store's shape keeps one. */
function asSession(web: WebAuthSession): AuthSession {
  return { ...web, refreshToken: "" };
}

async function persistSession(session: AuthSession | null) {
  if (WEB_COOKIE_AUTH || !session) await storage.remove(SESSION_KEY);
  else await storage.set(SESSION_KEY, JSON.stringify(session));
}

async function persistUser(user: User | null) {
  if (user) await storage.set(USER_KEY, JSON.stringify(user));
  else await storage.remove(USER_KEY);
}

async function persistOnboarding(progress: OnboardingProgress) {
  await storage.set(ONBOARDING_KEY, JSON.stringify(progress));
}

async function persistPendingEmail(email: string | null) {
  if (email) {
    await storage.set(
      PENDING_EMAIL_KEY,
      JSON.stringify({ email, at: Date.now() }),
    );
  } else {
    await storage.remove(PENDING_EMAIL_KEY);
  }
}

function parseStored<T>(value: string | null): T | null {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

/**
 * A cached user is only usable if it is actually a user.
 *
 * `parseStored` casts, it does not check, and `persistUser` will happily
 * `JSON.stringify` whatever it is given. Before `api/response.ts` rejected
 * non-JSON 2xx bodies, an access gateway's 200 `text/html` page was returned
 * as a `User` and written here — and a stringified 32KB HTML string
 * round-trips through JSON perfectly, so it comes back as a truthy value that
 * every `user?.field` read reports as `undefined`. `needsProfileSetup()` then
 * calls the profile incomplete and the router sends a fully signed-up person
 * to onboarding, on every launch, forever.
 *
 * The client fix stops new poisoning; this stops devices already carrying it
 * from staying broken after they update. Treat anything that is not a
 * user-shaped object as no cached user at all.
 */
function asStoredUser(value: unknown): User | null {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return null;
  }
  return typeof (value as { id?: unknown }).id === "string"
    ? (value as User)
    : null;
}

/**
 * Finish an onboarding this device believes it already walked through.
 *
 * A convenience, never a precondition — which is why its failure is swallowed.
 * The server refuses `onboardingComplete` without a date of birth on file, and
 * that refusal is CORRECT here: this path exists for a device whose stored
 * progress says permissions are done, and stored progress is not evidence that
 * the account it now belongs to ever answered the profile step.
 *
 * Letting the refusal escape put the launch into the outer catch, which — with
 * no cached user — publishes `status: "unavailable"`, the "we could not load
 * your profile" screen. That is false: `/users/me` had just answered. It would
 * have shown a retry that could never succeed, in place of the onboarding
 * screen that collects the missing date. Returning the user we already read
 * lets `needsProfileSetup` route them there instead.
 *
 * Only the REFUSAL is ignored. The first version of this was a bare catch,
 * which said something much wider — that no failure of this call means anything
 * — and a 401 does: it has already started sign-out by the time it is thrown, so
 * swallowing it let the launch overwrite `unauthenticated` with `authenticated`
 * against a session that was by then null. Everything except a 400 goes to the
 * outer catch, which knows what to do with each. See `lib/ignorable-refusal.ts`.
 */
async function resumeOnboardingCompletion(user: User): Promise<User> {
  try {
    return await usersApi.update({ onboardingComplete: true });
  } catch (error) {
    if (!isIgnorableRefusal(error)) throw error;
    return user;
  }
}

async function syncUserLocale(user: User): Promise<User> {
  const locale = getCurrentAppLanguage();
  if (user.locale === locale) return user;
  try {
    return await usersApi.update({ locale });
  } catch (error) {
    // The same rule as the resume above, and for the same reason: this is a
    // convenience, so being refused must not fail the launch — but a 401 here
    // has equally already started sign-out, and swallowing it would let
    // `hydrate` publish `authenticated` over a session being torn down.
    // Reviewed as one finding on the resume; this is its twin, one line later.
    if (!isIgnorableRefusal(error)) throw error;
    return user;
  }
}

/** Guards against re-entrant sign-out (a failing logout call can 401 → sign out again). */
let signingOut = false;

export const useAuthStore = create<AuthState>((set, get) => ({
  status: "loading",
  session: null,
  user: null,
  pendingEmail: null,
  onboarding: defaultOnboarding,

  async hydrate() {
    setUnauthorizedHandler(() => {
      void get().clearLocalSession();
    });
    // When any request 401s, try to rotate tokens with the stored refresh
    // token before giving up. This is what keeps the user signed in across
    // access-token expiry, until they explicitly sign out or the refresh
    // token is revoked/expired.
    setRefreshHandler(async () => {
      const session = get().session;
      // Web holds no refresh token in JS — the cookie is the credential, so
      // an existing session object (even tokenless) is enough to try.
      if (WEB_COOKIE_AUTH ? !session : !session?.refreshToken) return null;
      try {
        const next = WEB_COOKIE_AUTH
          ? asSession(await webAuthApi.refresh())
          : await authApi.refresh(session!.refreshToken);
        setAccessToken(next.accessToken);
        await persistSession(next);
        set({ session: next });
        return next.accessToken;
      } catch (err) {
        // Only give up (→ sign out) when the refresh token is *definitively*
        // rejected. Transient failures (network blip, server restart) are
        // rethrown so the current request fails without logging the user out;
        // a later request can retry the refresh and recover the session.
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    });

    // Before anything reads it: make sure the stored session is one the
    // stay-visible background task can still read when the phone is locked.
    // Runs from the foreground, where the old guard permits the read.
    await resealKeychain([SESSION_KEY]);

    try {
      const [rawSession, rawUser, rawOnboarding] = await Promise.all([
        storage.get(SESSION_KEY),
        storage.get(USER_KEY),
        storage.get(ONBOARDING_KEY),
      ]);
      const storedOnboarding =
        parseStored<Partial<OnboardingProgress>>(rawOnboarding);
      const onboarding = storedOnboarding
        ? { ...defaultOnboarding, ...storedOnboarding }
        : defaultOnboarding;

      let session: AuthSession | null = null;
      if (WEB_COOKIE_AUTH) {
        // A pre-cookie build may have left a session in localStorage; it is
        // exactly what must not live there, so it goes regardless of outcome.
        if (rawSession) await storage.remove(SESSION_KEY);
        try {
          session = asSession(await webAuthApi.refresh());
        } catch (err) {
          if (err instanceof ApiError) {
            // 401/403: no (or revoked) cookie — honestly signed out.
            set({ status: "unauthenticated", onboarding });
            return;
          }
          // Transient (offline, API restart): same doctrine as native below —
          // an unreachable API is "stay signed in", not "log out". A cached
          // user renders the app, and the TOKENLESS session below is what
          // makes recovery real: the 401 refresh handler refuses to run with
          // no session at all, so leaving it null here turned a boot-time
          // blip into a later false sign-out (review, 2026-09-02). With the
          // placeholder in place, the first authenticated request 401s, the
          // handler calls the cookie refresh, and the session heals.
          const cached = asStoredUser(parseStored<unknown>(rawUser));
          if (!cached) {
            set({ status: "unauthenticated", onboarding });
            return;
          }
          set({
            status: "authenticated",
            user: cached,
            session: { accessToken: "", refreshToken: "", userId: cached.id },
            onboarding,
          });
          return;
        }
      } else {
        if (!rawSession) {
          set({ status: "unauthenticated", onboarding });
          return;
        }
        session = parseStored<AuthSession>(rawSession);
      }
      if (!session) {
        set({ status: "unauthenticated", onboarding });
        return;
      }
      const cachedUser = asStoredUser(parseStored<unknown>(rawUser));
      setAccessToken(session.accessToken);
      // Make the session available to the refresh handler before the first
      // authenticated call, so an expired access token can be rotated inline.
      set({ session, user: cachedUser });

      // A stored session plus a cached user is enough to show the app. Leave
      // "loading" now and reconcile with the server in the background:
      // blocking the launch screen on a network round trip means a device with
      // no connectivity never gets in at all, and even a working one waits on
      // the splash for no reason. The failure branch below already treats an
      // unreachable API as "stay signed in", so this only changes *when* that
      // is decided, not what it decides.
      if (cachedUser) {
        set({ status: "authenticated", session, user: cachedUser, onboarding });
      }

      try {
        const user = await usersApi.me();
        const profileUser =
          onboarding.permissionsDone && !user.onboardingComplete
            ? await resumeOnboardingCompletion(user)
            : user;
        const syncedUser = await syncUserLocale(profileUser);
        await persistUser(syncedUser);
        set({
          status: "authenticated",
          session: get().session,
          user: syncedUser,
          onboarding,
        });
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          // No `set({ onboarding })` after this: the session is being discarded,
          // and `clearLocalSession` has just reset progress on purpose. Putting
          // the value read at the top of bootstrap back would restore the very
          // state that lets the next account skip onboarding.
          await get().clearLocalSession();
          return;
        }

        // Keep the user signed in across reloads even if the API is briefly
        // unavailable or a token refresh cannot complete. Only explicit
        // Sign out clears the stored session.
        if (cachedUser) {
          set({
            status: "authenticated",
            session: get().session,
            user: cachedUser,
            onboarding,
          });
          return;
        }

        // No cached user, and the API did not give us one. Everything below
        // this point would be a guess about the account, so say what is
        // actually true: the session stands, the profile could not be loaded.
        // Publishing `authenticated` with a null user here is what sent
        // signed-up people to onboarding.
        set({
          status: "unavailable",
          session: get().session,
          user: null,
          onboarding,
        });
      }
    } catch {
      set({ status: "unauthenticated" });
    }
  },

  async startEmail(email) {
    const normalized = email.trim().toLowerCase();
    await authApi.start(normalized, getCurrentAppLanguage());
    set({ pendingEmail: normalized });
    // On disk as well as in state: the user is about to leave the app to read
    // the code, and the app may not survive it.
    await persistPendingEmail(normalized);
  },

  async restorePendingEmail(candidate) {
    const current = get().pendingEmail;
    if (current) return current;

    const fromRoute = candidate?.trim().toLowerCase();
    if (fromRoute) {
      set({ pendingEmail: fromRoute });
      return fromRoute;
    }

    const stored = parseStored<{ email?: unknown; at?: unknown }>(
      await storage.get(PENDING_EMAIL_KEY),
    );
    const email = typeof stored?.email === "string" ? stored.email : null;
    const at = typeof stored?.at === "number" ? stored.at : 0;
    // Old enough that the code it belongs to is long gone, and restoring it
    // would only produce a confusing "expired" on a screen the user has no
    // memory of opening.
    if (!email || Date.now() - at > PENDING_EMAIL_TTL_MS) {
      if (stored) await persistPendingEmail(null);
      return null;
    }

    set({ pendingEmail: email });
    return email;
  },

  async verify(code) {
    const email = get().pendingEmail;
    if (!email) throw new MissingPendingEmailError();
    const session = WEB_COOKIE_AUTH
      ? asSession(
          await webAuthApi.verify(email, code.trim(), getCurrentAppLanguage()),
        )
      : await authApi.verify(email, code.trim(), getCurrentAppLanguage());
    setAccessToken(session.accessToken);
    await persistSession(session);
    const user = await syncUserLocale(await usersApi.me());
    await persistUser(user);
    set({ status: "authenticated", session, user, pendingEmail: null });
    await persistPendingEmail(null);
  },

  async signInWithGoogle(idToken) {
    const session = WEB_COOKIE_AUTH
      ? asSession(await webAuthApi.google(idToken, getCurrentAppLanguage()))
      : await authApi.google(idToken, getCurrentAppLanguage());
    setAccessToken(session.accessToken);
    await persistSession(session);
    const user = await syncUserLocale(await usersApi.me());
    await persistUser(user);
    set({ status: "authenticated", session, user, pendingEmail: null });
  },

  async signInWithApple(identityToken, fullName, nonce) {
    const session = WEB_COOKIE_AUTH
      ? asSession(
          await webAuthApi.apple(
            identityToken,
            fullName,
            nonce,
            getCurrentAppLanguage(),
          ),
        )
      : await authApi.apple(
          identityToken,
          fullName,
          nonce,
          getCurrentAppLanguage(),
        );
    setAccessToken(session.accessToken);
    await persistSession(session);
    const user = await syncUserLocale(await usersApi.me());
    await persistUser(user);
    set({ status: "authenticated", session, user, pendingEmail: null });
  },

  async refreshUser() {
    if (get().status !== "authenticated") return;
    const user = await syncUserLocale(await usersApi.me());
    await persistUser(user);
    set({ user });
  },

  async setProfile(patch) {
    const user = await usersApi.update(patch);
    await persistUser(user);
    set({ user });
  },

  async setAvatar(avatarConfig) {
    const user = await usersApi.updateAvatar(avatarConfig);
    await persistUser(user);
    set({ user });
  },

  async setDevPlan(plan) {
    // Guard so this can never change a real user's plan in a production build.
    if (!__DEV__) return;
    // Hits the dev-only server endpoint that actually sets the plan in the DB,
    // so quota/export/limits and every `plan === 'plus'` gate genuinely unlock
    // end to end. The server route is disabled in production too.
    const user = await usersApi.setDevPlan(plan);
    await persistUser(user);
    set({ user });
  },

  async setMode(mode) {
    const user = await usersApi.update({ mode });
    await persistUser(user);
    const onboarding = { ...get().onboarding, modeDone: true };
    await persistOnboarding(onboarding);
    set({ user, onboarding });
  },

  async skipMode() {
    const onboarding = { ...get().onboarding, modeDone: true };
    await persistOnboarding(onboarding);
    set({ onboarding });
  },

  async completePermissions() {
    const user = await usersApi.update({ onboardingComplete: true });
    const onboarding = {
      ...get().onboarding,
      permissionsDone: true,
      modeDone: true,
    };
    await persistUser(user);
    await persistOnboarding(onboarding);
    set({ user, onboarding });
  },

  async clearLocalSession() {
    // Started FIRST, deliberately un-awaited until the end. The registered
    // handlers run synchronously up to their first await, and session-store's
    // reset does everything that matters in that prefix: the session epoch
    // bumps and the in-memory lists clear the moment sign-out begins — not
    // after the storage writes below finish, which left a window where a
    // response already on the wire (carrying this account's token in its
    // baked headers) was still welcomed into the store. It also means the
    // in-memory state is clean even if one of those storage writes throws,
    // which previously skipped the reset entirely.
    const sessionStateReset = resetRegisteredSessionState();
    try {
      setAccessToken(null);
      // Flip the status before awaiting anything. This is what routes the app to
      // the welcome screen, and the awaits below are disk writes — leaving them
      // in front means a dead session keeps rendering the signed-in tabs, which
      // go on firing requests that can only fail, for as long as storage takes.
      set({
        status: "unauthenticated",
        session: null,
        user: null,
        pendingEmail: null,
        // In memory as well as on disk. Leaving the old progress in state means a
        // second account signed in during this same app session skips the screens
        // that collect a date of birth, which is the disk bug reached without a
        // relaunch.
        onboarding: defaultOnboarding,
      });
      await persistSession(null);
      await persistUser(null);
      // Whoever signs in next must not inherit the previous person's pending
      // address — the same rule the onboarding record below follows.
      await persistPendingEmail(null);
      // Onboarding progress goes too. `ONBOARDING_KEY` is device-global, not
      // per-account, and leaving it behind meant the NEXT account to sign in on
      // this device inherited `permissionsDone` — so bootstrap PATCHed it straight
      // to `onboardingComplete: true` without ever asking for a date of birth, and
      // it never saw the onboarding screens that collect one. The server refuses
      // that now; this stops a legitimate signup meeting the refusal at launch
      // instead of being asked the question.
      await persistOnboarding(defaultOnboarding);
      await clearPresenceAnchor();
      // The presence anchor goes for the same reason onboarding does: the key
      // is device-global, and the next account to sign in must not have its
      // background wakes classified against the PREVIOUS person's last
      // published position. Bounded internally, so a wedged keychain cannot
      // stall sign-out.
    } finally {
      // In a finally so the promise is observed even when a persist above
      // throws — otherwise its own failure would surface as an unhandled
      // rejection from a function that already reported a different error.
      await sessionStateReset;
    }
  },

  async signOut() {
    if (signingOut) return;
    signingOut = true;
    try {
      // Sign-out works like account deletion: revoke server-side FIRST, and
      // only clear the local session once the server confirms (204). The server
      // returns 204 only when BOTH the refresh session and this session's push
      // registrations are gone; a partial failure is a 503. So a failure here
      // throws, `clearLocalSession` is never reached, and the user stays signed
      // in to retry — never half-out with a device still receiving previews.
      // There is deliberately no offline queue: a sign-out that did not reach
      // the server did not happen, exactly like a delete that did not land.
      if (WEB_COOKIE_AUTH) {
        // The HttpOnly cookie is the credential; the endpoint clears it only on
        // success, so a 503/401 leaves the browser session intact.
        await webAuthApi.logout();
      } else {
        // Revoke by the refresh token, not an access token: a retry after a
        // partial failure still completes even once the Redis entry is gone.
        // No token = nothing the server can revoke, so we must NOT clear local
        // (that would strand a push registration). Throw and stay signed in;
        // a relaunch's /users/me→401 path signs out cleanly instead.
        const refreshToken = get().session?.refreshToken;
        if (!refreshToken) throw new Error("Cannot sign out: no refresh token");
        await authApi.revoke(refreshToken);
      }
      // Reached only on a confirmed 204 — both cleanups done server-side.
      await get().clearLocalSession();
    } finally {
      signingOut = false;
    }
  },

  /**
   * Deactivate or reactivate — the reversible alternative to deleting.
   *
   * Unlike deletion this does NOT end the session: a deactivated user is still
   * signed in and still using Practice, goals and reflections. Only the social
   * half of the app closes, and the same button opens it again.
   */
  async setAccountDeactivated(deactivated: boolean) {
    const { deactivatedAt } = deactivated
      ? await usersApi.deactivateAccount()
      : await usersApi.reactivateAccount();
    const user = get().user;
    if (!user) return;
    const next = { ...user, deactivatedAt };
    set({ user: next });
    await persistUser(next);
  },

  /**
   * Open to chat — tell the contacts I am already talking to that I am free now.
   *
   * Lives here rather than in the session store, beside `setAccountDeactivated`
   * and for the same reason: `openToChatUntil` is a field on MY user record,
   * self-only on the wire, and it arrives with the `/users/me` bootstrap. One
   * owner means the toggle and the value the app booted with cannot disagree —
   * which they would the moment the list screen kept its own copy.
   *
   * It calls `chatApi` despite living in the auth store, because the endpoint
   * is `PUT /chat/open`: the audience for this signal is derived from
   * conversations, so the server put it with the conversations. Following the
   * data rather than the file name.
   *
   * The lease stored is the one the SERVER returned, never the duration asked
   * for. The screen renders remaining time by comparing it to the clock, so a
   * slow request or a skewed device clock still shows what the peer sees.
   */
  async setOpenToChat(durationMinutes: number | null) {
    const { openUntil } = await chatApi.setOpen(durationMinutes);
    const user = get().user;
    // No local user means no record to patch. The server already applied it,
    // and the next `/users/me` will carry it — the same shape as
    // `setAccountDeactivated`, which returns here for the same reason.
    if (!user) return;
    const next = { ...user, openToChatUntil: openUntil };
    set({ user: next });
    // Persisted, so a restart inside the lease still paints the toggle on
    // before any request completes.
    await persistUser(next);
  },

  async deleteAccount() {
    // Delete server-side first; only drop the local session once the account is
    // actually gone, so a failure leaves the user signed in to retry.
    await usersApi.deleteAccount();
    await get().clearLocalSession();
  },
}));
